import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const CACHE_KEY = "public_meta";

// No hardcoded product name in the fallback — an unnamed deployment shows
// this plain, generic word (also index.html's static <title>) rather than
// ever flashing a name nobody chose. Whatever the owner sets in Platform
// settings is the only "brand" this app has.
const FALLBACK = {
  platform_name: "Messages",
  logo: "",
  favicon: "",
  needs_setup: false,
  public: { login: {}, landing: {} },
};

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Public branding for the sign-in and landing pages.
 *
 * Starts from what was seen last time, so the name doesn't briefly show the
 * default and then correct itself on every refresh. Still refetches in the
 * background, so a change made in settings appears on the next load.
 */
/** Swap the browser tab icon for the uploaded one. */
function applyFavicon(href) {
  if (!href) return;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

export function usePublicMeta() {
  const [meta, setMeta] = useState(() => {
    const cached = readCache();
    // Applied from the cache first, so the tab doesn't flash the default
    // icon or title — index.html's own inline script already did this
    // ahead of React even loading; this just keeps it in sync as state.
    if (cached?.favicon) applyFavicon(cached.favicon);
    if (cached?.platform_name) document.title = cached.platform_name;
    return cached || FALLBACK;
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get("/meta")
      .then(({ data }) => {
        if (cancelled) return;
        setMeta(data);
        applyFavicon(data.favicon);
        document.title = data.platform_name || FALLBACK.platform_name;
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch {
          /* private browsing — it just won't be remembered */
        }
      })
      .catch(() => {
        /* keep whatever we have */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return meta;
}
